/**
 * Recent Activities Card
 *
 * Compact summary of activities for a given period + scope. Used:
 *  - On the Overview dashboard (all scopes, no toggle) — matches the original
 *    inline block this component was extracted from.
 *  - On Scope 1 / Scope 2 pages (scope-filtered, with a Latest/Top toggle) —
 *    sits alongside TopEmissionSourcesCard in a half-width grid.
 */

import { useState, type MouseEvent } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { ArrowRight, Clock, Inbox, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { Badge } from '@g17eco/ui-modern/components/ui/badge';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { TabNav } from '@g17eco/ui-modern/components/ui/tab-nav';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { useGetActivitiesQuery } from '../api/emissions';
import { ACTIVITY_TYPE_LABELS } from '../config/activityTypeBranding';
import { SCOPE_LUCIDE_ICONS, SCOPE_TAILWIND } from '../config/scopeBranding';
import type { ActivityRecord, EmissionScope } from '../types/emissions';
import { getActivitySubtype, getActivityUnitLabel } from '../utils/activityUtils';
import { formatTimeAgo } from '../../../utils/date';

type ActivityView = 'latest' | 'top';

interface RecentActivitiesCardProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
  /** Omit for "all scopes" (Overview behaviour). */
  scope?: EmissionScope;
  /** Default 5. */
  limit?: number;
  /**
   * Title (string) or factory (view) => string. Defaults to "Recent Activities".
   */
  title?: string | ((view: ActivityView) => string);
  /**
   * "View all" URL — static string, or factory so callers can encode
   * sort params that match the current view.
   */
  viewAllUrl: string | ((view: ActivityView) => string);
  /** Called when a row is clicked; typically opens CalculationDetailsDrawer. */
  onViewCalculation?: (activity: ActivityRecord) => void;
  /**
   * Optional action button shown inside the empty state (e.g. "Add activity"
   * on the Overview). Keeps parity with the original inline block.
   */
  emptyAction?: { label: string; to: string; icon?: React.ReactNode };
}

const SCOPE_LABELS: Record<string, string> = {
  scope1: 'Scope 1',
  scope2: 'Scope 2',
  scope3: 'Scope 3',
};

const sortParams = (view: ActivityView) =>
  view === 'top'
    ? { sort: 'calculatedCO2e', sortDir: 'desc' as const }
    : { sort: 'created', sortDir: 'desc' as const };

export const RecentActivitiesCard = ({
  initiativeId,
  periodStart,
  periodEnd,
  includeChildren,
  scope,
  limit = 5,
  title = 'Recent Activities',
  viewAllUrl,
  onViewCalculation,
  emptyAction,
}: RecentActivitiesCardProps) => {
  const history = useHistory();
  const [view, setView] = useState<ActivityView>('latest');

  const { data, isLoading } = useGetActivitiesQuery(
    {
      initiativeId,
      limit,
      periodStart,
      periodEnd,
      includeChildren,
      scope,
      ...sortParams(view),
    },
    { skip: !initiativeId },
  );

  const activities = data?.data ?? [];
  const resolvedTitle = typeof title === 'function' ? title(view) : title;
  const resolvedUrl = typeof viewAllUrl === 'function' ? viewAllUrl(view) : viewAllUrl;

  return (
    <Card className='tw:h-full tw:flex tw:flex-col'>
      <CardHeader>
        <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
          <CardTitle className='tw:flex tw:items-center tw:gap-2'>
            <Clock className='tw:size-4 tw:text-muted-foreground' />
            {resolvedTitle}
          </CardTitle>
          <div className='tw:flex tw:items-center tw:gap-3' style={{ marginTop: '-0.25rem', marginBottom: '-0.5rem' }}>
            <TabNav
              variant='toggle'
              size='xs'
              activeTab={view}
              onChange={setView}
              tabs={[
                { key: 'latest', label: 'Latest' },
                { key: 'top', label: 'Top' },
              ]}
            />
            <Button
              variant='link'
              size='sm'
              onClick={() => history.push(resolvedUrl)}
            >
              View all
              <ArrowRight />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className='tw:flex-1'>
        {isLoading ? (
          <div className='tw:flex tw:justify-center tw:py-8'>
            <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
          </div>
        ) : activities.length === 0 ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:py-8 tw:text-muted-foreground'>
            <Inbox className='tw:size-8 tw:mb-2 tw:opacity-50' />
            <p className={cn('tw:text-sm', emptyAction ? 'tw:mb-3' : 'tw:mb-0')}>
              No activities recorded in this period
            </p>
            {emptyAction ? (
              <Link to={emptyAction.to}>
                <Button variant='outline' size='sm'>
                  {emptyAction.icon}
                  {emptyAction.label}
                </Button>
              </Link>
            ) : null}
          </div>
        ) : (
          <div className='tw:divide-y tw:divide-gray-300'>
            {activities.map((activity) => (
              <ActivityRow
                key={activity._id}
                activity={activity}
                onClick={onViewCalculation}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface ActivityRowProps {
  activity: ActivityRecord;
  onClick?: (activity: ActivityRecord) => void;
}

const ActivityRow = ({ activity, onClick }: ActivityRowProps) => {
  const subtype = activity.name ? activity.name : getActivitySubtype(activity) || '';
  const ScopeIcon = SCOPE_LUCIDE_ICONS[activity.scope];
  const scopeTheme = SCOPE_TAILWIND[activity.scope];

  const handleClick = onClick
    ? (e: MouseEvent) => {
        e.preventDefault();
        onClick(activity);
      }
    : undefined;

  return (
    <div
      className={cn(
        'tw:flex tw:justify-between tw:items-start tw:py-3 first:tw:pt-0 last:tw:pb-0',
        onClick && 'tw:cursor-pointer tw:hover:bg-muted/40 tw:-mx-2 tw:px-2 tw:rounded-md',
      )}
      onClick={handleClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick(activity);
            }
          }
        : undefined}
        style={{ height: '4rem' }}
    >
      <div className='tw:flex tw:items-start tw:gap-3 tw:h-10'>
        <div
          className={cn(
            'tw:rounded-xl tw:flex tw:items-center tw:justify-center tw:size-9 tw:shrink-0',
            scopeTheme?.iconBg ?? 'tw:bg-gray-500/10',
          )}
        >
          {ScopeIcon ? (
            <ScopeIcon className={cn('tw:size-4', scopeTheme?.icon)} />
          ) : null}
        </div>
        <div>
          <div className='tw:text-sm tw:font-medium tw:capitalize'>
            {ACTIVITY_TYPE_LABELS[activity.activityType] ?? activity.activityType}
          </div>
          <div className='tw:text-xs tw:text-muted-foreground'>
            {activity.quantity.toLocaleString()} {getActivityUnitLabel(activity.unit)}
            {subtype && <span className='tw:ml-1'>&#8226; {subtype}</span>}
          </div>
        </div>
      </div>
      <div className='tw:text-right tw:shrink-0'>
        <div className='tw:text-sm tw:font-medium tw:tabular-nums'>
          {activity.calculatedCO2e != null ? (
            <>
              {(activity.calculatedCO2e / 1000).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}{' '}
              <span className='tw:text-xs tw:text-muted-foreground tw:font-normal'>tCO₂e</span>
            </>
          ) : (
            <span className='tw:text-xs tw:text-muted-foreground'>Pending</span>
          )}
        </div>
        <div className='tw:text-xs tw:text-muted-foreground'>
          {formatTimeAgo(activity.created)}
          <Badge
            variant='secondary'
            className={cn(
              'tw:mb-1 tw:text-[10px] pb-0',
              scopeTheme?.iconBg ?? 'tw:bg-gray-500/10',
              scopeTheme?.icon ?? 'tw:text-gray-700',
            )}
          >
            {SCOPE_LABELS[activity.scope] || activity.scope}
          </Badge>
        </div>
      </div>
    </div>
  );
};
